import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { clientAPI, taskAPI, analyticsAPI, opportunityAPI, calendarAPI } from '../../utils/crmApi';
import { useCRM } from '../../contexts/CRMContext';
import { useBranding } from '../../contexts/BrandingContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import dataService from '../../services/dataService';
import FeatureGate from '../../components/crm/FeatureGate';
import LocationMapComponent from '../../components/crm/LocationMapComponent';
import { messagesAPI, formsAPI } from '../../utils/crmApi';
import { useDialog, ConfirmationDialog } from '../../components/kibo-ui/dialog';
import { UsageLimit } from '../../components/crm/FeatureGate';
import { ActionButtonGroup, ActionConfirmationDialog } from '../ui/action-buttons';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Separator } from '../ui/separator';
import {
    UserIcon,
    ArrowLeftIcon,
    ArrowRightIcon,
    PencilIcon,
    TrashIcon,
    BuildingOfficeIcon,
    ChartBarIcon,
    CurrencyDollarIcon,
    EnvelopeIcon,
    PhoneIcon,
    MapPinIcon,
    CalendarIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    LightBulbIcon,
    PlusIcon,
    EyeIcon,
    EyeSlashIcon,
    UserGroupIcon,
    GlobeAltIcon,
    SparklesIcon,
    BellIcon,
    DocumentTextIcon,
    PaperClipIcon,
    EllipsisHorizontalIcon,
    ChevronDownIcon
} from '@heroicons/react/24/outline';
import MessageComposer from './MessageComposer';
import MessageList from './MessageList';
import { globalMessageTracking } from '../../hooks/useMessagePolling';
import toast from 'react-hot-toast';
import { format, parseISO, isAfter, isBefore, addDays } from 'date-fns';
import { StatusBadge, StatusDropdown } from '../ui/status-components';
import ClientStatusValidationModal from '../ui/client-status-validation-modal';
import { validateClientForTaskAssignment } from '../../utils/clientValidation';
import { EnhancedDonutChart } from '../ui/enhanced-charts';

const ClientDetailEnhanced = () => {
    const { id: clientId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { branding } = useBranding();
    const { currentAgency } = useCRM();
    const { hasFeature, canPerformAction } = useSubscription();
    const { openDialog } = useDialog();
    
    // Check if user came from account detail view and get tab parameter
    const searchParams = new URLSearchParams(location.search);
    const fromAccount = searchParams.get('fromAccount');
    const tabParam = searchParams.get('tab');
    const conversationParam = searchParams.get('conversation');
    
    const [client, setClient] = useState(null);
    const [userTasks, setUserTasks] = useState([]);
    const [opportunities, setOpportunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showStatusValidationModal, setShowStatusValidationModal] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(tabParam || 'overview');
    
    // Switch to messages tab if conversation parameter is present
    useEffect(() => {
        if (conversationParam && activeTab !== 'messages') {
            setActiveTab('messages');
        }
    }, [conversationParam, activeTab]);
    
    // Clear global message tracking when switching away from messages tab
    useEffect(() => {
        if (activeTab !== 'messages') {
            // Clear selectedConversationId when user switches to another tab
            globalMessageTracking.selectedConversationId = null;
            if (process.env.NODE_ENV === 'development') {
                console.log('[ClientDetailEnhanced] Tab changed away from messages - clearing selectedConversationId');
            }
        }
    }, [activeTab]);
    
    const [unreadMessageCount, setUnreadMessageCount] = useState(0);
    
    // Email sending state
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [emailForm, setEmailForm] = useState({
        templateId: '',
        recipientEmail: '',
        recipientName: '',
        variables: {}
    });
    const [sendingEmail, setSendingEmail] = useState(false);

    // AI Copilot state
    const [aiSuggestions, setAiSuggestions] = useState([]);
    const [showAiPanel, setShowAiPanel] = useState(false);

    // Action handling state
    const [actionLoading, setActionLoading] = useState({});
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [showFormInvite, setShowFormInvite] = useState(false);
    const [availableForms, setAvailableForms] = useState([]);
    const [selectedFormId, setSelectedFormId] = useState('');
    const [showAppointmentModal, setShowAppointmentModal] = useState(false);
    const [appointmentData, setAppointmentData] = useState({
        title: '',
        description: '',
        startAt: '',
        endAt: '',
        location: '',
        type: 'appointment'
    });
    const [actionsMenuOpen, setActionsMenuOpen] = useState(false);

    const statusConfig = {
        'Prospect': {
            color: 'yellow',
            bgColor: 'bg-yellow-50',
            borderColor: 'border-yellow-200',
            textColor: 'text-yellow-800',
            icon: ClockIcon,
            nextStatus: 'Active',
            recommendations: [
                'Schedule an initial consultation call',
                'Send personalized welcome email with next steps',
                'Assign relevant tasks based on their profile',
                'Set up regular follow-up reminders',
                'Gather additional information about their needs'
            ]
        },
        'Active': {
            color: 'green',
            bgColor: 'bg-green-50',
            borderColor: 'border-green-200',
            textColor: 'text-green-800',
            icon: CheckCircleIcon,
            nextStatus: null,
            recommendations: [
                'Monitor task completion progress regularly',
                'Provide ongoing support and guidance',
                'Identify opportunities for additional services',
                'Maintain regular communication',
                'Gather feedback for service improvement'
            ]
        },
        'Paused': {
            color: 'orange',
            bgColor: 'bg-orange-50',
            borderColor: 'border-orange-200',
            textColor: 'text-orange-800',
            icon: ExclamationTriangleIcon,
            nextStatus: 'Active',
            recommendations: [
                'Understand the reason for pausing',
                'Address any concerns or issues',
                'Provide additional support if needed',
                'Re-engage with personalized approach',
                'Offer flexible solutions or alternatives'
            ]
        },
        'Closed Lost': {
            color: 'red',
            bgColor: 'bg-red-50',
            borderColor: 'border-red-200',
            textColor: 'text-red-800',
            icon: ExclamationTriangleIcon,
            nextStatus: 'Prospect',
            recommendations: [
                'Conduct exit interview to understand reasons',
                'Analyze what went wrong',
                'Implement lessons learned',
                'Consider re-engagement in the future',
                'Update qualification criteria'
            ]
        },
        'Churned': {
            color: 'red',
            bgColor: 'bg-red-50',
            borderColor: 'border-red-200',
            textColor: 'text-red-800',
            icon: ExclamationTriangleIcon,
            nextStatus: 'Prospect',
            recommendations: [
                'Analyze churn reasons and patterns',
                'Implement retention strategies',
                'Consider win-back campaigns',
                'Improve service quality',
                'Learn from the experience'
            ]
        }
    };

    const statuses = ['Prospect', 'Active', 'Paused', 'Closed Lost', 'Churned'];

    const fetchOpportunities = async () => {
        try {
            console.log('🔍 Fetching opportunities for client:', clientId);
            const response = await opportunityAPI.getByClient(clientId);
            console.log('🔍 Client opportunities response:', response.data);
            setOpportunities(response.data);
        } catch (error) {
            console.error('Error fetching client opportunities:', error);
            // Don't show toast error for opportunities as it's not critical
        }
    };

    useEffect(() => {
        if (clientId && clientId !== 'undefined') {
            fetchClientDetails();
            fetchEmailTemplates();
            generateAiSuggestions();
            fetchOpportunities();
            messagesAPI.getUnreadCount(clientId)
                .then(res => {
                    if (res.data?.success) setUnreadMessageCount(res.data.count || 0);
                }).catch(() => {});
        } else if (clientId === 'undefined') {
            setError('Invalid client ID');
            setLoading(false);
        }
    }, [clientId]);

    // Add timeout to prevent infinite loading
    useEffect(() => {
        if (loading) {
            const timeout = setTimeout(() => {
                if (loading) {
                    console.error('Client loading timeout - showing error');
                    setError('Loading timeout. Please try again.');
                    setLoading(false);
                }
            }, 10000); // 10 second timeout

            return () => clearTimeout(timeout);
        }
    }, [loading]);

    const fetchClientDetails = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Try using dataService first (with caching)
            const clientData = await dataService.getClient(clientId);
            console.log('📊 Client data from dataService:', clientData);
            console.log('📊 Client data type:', typeof clientData);
            console.log('📊 Client data keys:', Object.keys(clientData || {}));
            
            if (clientData) {
                console.log('📊 Setting client from dataService:', clientData);
                // dataService returns axios response, so we need response.data
                const actualClientData = clientData.data?.data || clientData.data;
                console.log('📊 Actual client data:', actualClientData);
                console.log('🌍 Location fields:', {
                    countryNationality: actualClientData?.countryNationality,
                    countryResidence: actualClientData?.countryResidence,
                    hasNationality: !!actualClientData?.countryNationality,
                    hasResidence: !!actualClientData?.countryResidence,
                    nationalityType: typeof actualClientData?.countryNationality,
                    residenceType: typeof actualClientData?.countryResidence
                });
                setClient(actualClientData);
                setUserTasks(actualClientData.userTasks || []);
            } else {
                // Fallback to direct API call
                const response = await clientAPI.getById(clientId);
                console.log('📊 Client data from direct API:', response.data);
                console.log('📊 Response status:', response.status);
                console.log('📊 Response headers:', response.headers);
                
                if (response.data && response.data.success) {
                    console.log('📊 Setting client from direct API:', response.data);
                    // The API returns { success: true, data: clientObject }
                    const actualClientData = response.data.data;
                    console.log('📊 Actual client data from API:', actualClientData);
                    console.log('🌍 Location fields:', {
                        countryNationality: actualClientData?.countryNationality,
                        countryResidence: actualClientData?.countryResidence,
                        hasNationality: !!actualClientData?.countryNationality,
                        hasResidence: !!actualClientData?.countryResidence,
                        nationalityType: typeof actualClientData?.countryNationality,
                        residenceType: typeof actualClientData?.countryResidence,
                        allKeys: Object.keys(actualClientData || {})
                    });
                    setClient(actualClientData);
                    setUserTasks(actualClientData.userTasks || []);
                } else {
                    throw new Error('No client data received');
                }
            }
            
            // Additional debugging
            console.log('📊 Client data after setting:', {
                clientData,
                clientState: client
            });
            
            // Force a re-render to see if the client data is properly set
            setTimeout(() => {
                console.log('📊 Client state after timeout:', client);
            }, 100);
        } catch (error) {
            console.error('Error fetching client details:', error);
            setError('Failed to fetch client details. Please try again.');
            
            // Don't use mock data - let the error show
            console.log('API Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
        } finally {
            setLoading(false);
        }
    };

    const generateAiSuggestions = async () => {
        if (!hasFeature('aiCopilot')) return;

        try {
            // Mock AI suggestions - replace with actual AI API call
            const mockSuggestions = [
                {
                    id: 1,
                    type: 'action',
                    priority: 'high',
                    title: 'Follow up on overdue task',
                    description: 'Client has an overdue "Document Review" task that needs attention.',
                    action: 'Send reminder email',
                    icon: BellIcon,
                    color: 'red'
                },
                {
                    id: 2,
                    type: 'insight',
                    priority: 'medium',
                    title: 'Engagement opportunity',
                    description: 'Client has been inactive for 5 days. Consider sending a check-in email.',
                    action: 'Send engagement email',
                    icon: EnvelopeIcon,
                    color: 'yellow'
                },
                {
                    id: 3,
                    type: 'recommendation',
                    priority: 'low',
                    title: 'Upsell opportunity',
                    description: 'Based on client profile, they might benefit from additional services.',
                    action: 'Schedule consultation',
                    icon: SparklesIcon,
                    color: 'blue'
                }
            ];

            setAiSuggestions(mockSuggestions);
        } catch (error) {
            console.error('Error generating AI suggestions:', error);
        }
    };

    const handleStatusChange = async (newStatus) => {
        try {
            await clientAPI.update(clientId, { status: newStatus });
            setClient(prev => ({ ...prev, status: newStatus }));
            toast.success('Client status updated successfully');
        } catch (error) {
            console.error('Error updating client status:', error);
            toast.error('Failed to update client status');
        }
    };

    const handleConvertToActive = async () => {
        try {
            await clientAPI.update(clientId, { 
                status: 'Active',
                linkedUserId: client.linkedUserId 
            });
            setClient(prev => ({ ...prev, status: 'Active' }));
            toast.success('Client converted to active successfully');
        } catch (error) {
            console.error('Error converting client:', error);
            toast.error('Failed to convert client to active');
        }
    };

    const handleAssignTasksClick = () => {
        if (!client) return;
        
        const validation = validateClientForTaskAssignment(client);
        
        if (validation.isValid) {
            // Client is active, proceed directly to task assignment
            navigate('/tasks/new', { 
                state: { 
                    clientId: client._id,
                    clientName: getClientName(client),
                    clientEmail: client.email
                }
            });
        } else {
            // Client is not active, show validation modal
            setShowStatusValidationModal(true);
        }
    };

    const handleStatusUpdated = (updatedClient) => {
        setClient(updatedClient);
    };

    const handleProceedToTaskAssignment = (clientData, bypassValidation = false) => {
        navigate('/tasks/new', { 
            state: { 
                clientId: clientData._id,
                clientName: getClientName(clientData),
                clientEmail: clientData.email,
                bypassValidation
            }
        });
    };

    const handleTaskStatusUpdate = async (taskId, newStatus) => {
        try {
            console.log('🔄 Updating task status:', { taskId, newStatus });
            const response = await taskAPI.update(taskId, { status: newStatus });
            console.log('📡 API response:', response.data);
            
            if (response.data.success) {
                // Update the task in the local state
                setUserTasks(prev => prev.map(task => 
                    task._id === taskId ? { ...task, status: newStatus } : task
                ));
                
                // Also update the client.userTasks to keep everything in sync
                if (client?.userTasks) {
                    setClient(prev => ({
                        ...prev,
                        userTasks: prev.userTasks.map(task => 
                            task._id === taskId ? { ...task, status: newStatus } : task
                        )
                    }));
                }
                
                toast.success('Task status updated successfully');
            } else {
                console.error('❌ API returned success: false:', response.data);
                toast.error('Failed to update task status');
            }
        } catch (error) {
            console.error('❌ Failed to update task status:', error);
            toast.error('Failed to update task status');
        }
    };

    const fetchEmailTemplates = async () => {
        try {
            const response = await clientAPI.getEmailTemplates();
            if (response.data.success) {
                setEmailTemplates(response.data.templates || []);
            }
        } catch (error) {
            console.error('Error fetching email templates:', error);
        }
    };

    const handleOpenEmailModal = () => {
        setEmailForm({
            templateId: '',
            recipientEmail: client?.email || '',
            recipientName: client?.fullName || '',
            variables: {}
        });
        setShowEmailModal(true);
    };

    const handleEmailFormChange = (field, value) => {
        if (field === 'templateId') {
            const template = emailTemplates.find(t => t._id === value);
            setSelectedTemplate(template);
            
            const autoVariables = {
                'client.name': getClientName(client) || client?.fullName || '',
                'client.email': client?.email || '',
                'client.company': client?.accountId?.companyName || 'N/A',
                'account.name': client?.accountId?.companyName || 'N/A',
                'account.contact': client?.accountId?.contactName || 'N/A',
                'partner.name': client?.clientPartner?.email || 'N/A',
                'current.date': new Date().toLocaleDateString(),
                'current.time': new Date().toLocaleTimeString()
            };
            
            setEmailForm(prev => ({
                ...prev,
                [field]: value,
                variables: autoVariables
            }));
        } else {
            setEmailForm(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const handleSendEmail = async () => {
        try {
            setSendingEmail(true);
            const emailData = {
                templateId: emailForm.templateId,
                clientId: clientId,
                recipientEmail: emailForm.recipientEmail,
                recipientName: emailForm.recipientName,
                variables: emailForm.variables
            };

            const response = await clientAPI.sendEmail(emailData);
            if (response.data.success) {
                toast.success('Email sent successfully!');
                setShowEmailModal(false);
                setEmailForm({
                    templateId: '',
                    recipientEmail: '',
                    recipientName: '',
                    variables: {}
                });
                setSelectedTemplate(null);
            } else {
                throw new Error(response.data.message || 'Failed to send email');
            }
        } catch (error) {
            console.error('Error sending email:', error);
            toast.error('Failed to send email: ' + error.message);
        } finally {
            setSendingEmail(false);
        }
    };

    const handleDeleteClient = () => {
        console.log('🗑️ Delete button clicked');
        console.log('🗑️ Client ID:', clientId);
        console.log('🗑️ Client name:', client?.name);
        
        openDialog({
            title: 'Delete Client',
            description: `Are you sure you want to delete ${getClientName(client) || 'this client'}? This action cannot be undone and will remove all associated data.`,
            confirmText: 'Delete Client',
            cancelText: 'Cancel',
            variant: 'danger',
            onConfirm: async () => {
                console.log('🗑️ Delete confirmed, calling API...');
                try {
                    await clientAPI.delete(clientId);
                    console.log('🗑️ Delete successful');
                    toast.success('Client deleted successfully');
                    if (fromAccount) {
                        navigate(`/accounts/${fromAccount}`);
                    } else {
                        navigate('/clients');
                    }
                } catch (error) {
                    console.error('Error deleting client:', error);
                    toast.error('Failed to delete client: ' + error.message);
                }
            }
        });
    };

    // Enhanced action handler for improved action buttons
    const handleCreateAppointment = () => {
        if (!client) {
            toast.error('No client selected');
            return;
        }

        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(10, 0, 0, 0);
        
        const endTime = new Date(tomorrow);
        endTime.setHours(11, 0, 0, 0);

        setAppointmentData({
            title: `Appointment with ${getClientName(client)}`,
            description: '',
            startAt: tomorrow.toISOString().slice(0, 16),
            endAt: endTime.toISOString().slice(0, 16),
            location: '',
            type: 'appointment'
        });
        setShowAppointmentModal(true);
    };

    const handleSaveAppointment = async () => {
        try {
            if (!clientId) {
                toast.error('No client selected');
                return;
            }

            if (!appointmentData.title.trim()) {
                toast.error('Appointment title is required');
                return;
            }

            if (!appointmentData.startAt || !appointmentData.endAt) {
                toast.error('Start and end times are required');
                return;
            }

            const eventData = {
                ...appointmentData,
                clientId: clientId,
                startAt: new Date(appointmentData.startAt).toISOString(),
                endAt: new Date(appointmentData.endAt).toISOString(),
                type: 'appointment',
                status: 'scheduled'
            };

            await calendarAPI.create(eventData);
            toast.success('Appointment created successfully!');
            setShowAppointmentModal(false);
            setAppointmentData({
                title: '',
                description: '',
                startAt: '',
                endAt: '',
                location: '',
                type: 'appointment'
            });
        } catch (error) {
            console.error('Failed to create appointment:', error);
            toast.error('Failed to create appointment');
        }
    };

    const handleAction = async (actionType, actionData) => {
        console.log('Action triggered:', actionType, actionData);
        
        setActionLoading(prev => ({ ...prev, [actionType]: true }));
        
        try {
            switch (actionType) {
                case 'sendEmail':
                    handleOpenEmailModal();
                    break;
                case 'edit':
                    navigate(`/clients/${clientId}/edit`, { 
                        state: { from: 'client-detail', clientId } 
                    });
                    break;
                case 'workspace':
                    navigate(`/clients/${clientId}/workspace`);
                    break;
                case 'journey':
                    navigate(`/relocation-journey/${clientId}`);
                    break;
                case 'invite': {
                    if (!client) break;
                    openDialog({
                        title: 'Invite Client to Portal',
                        description: `Send an invitation email to ${getClientName(client)} (${client.email}) to join the Client Portal?`,
                        confirmText: 'Send Invitation',
                        cancelText: 'Cancel',
                        variant: 'info',
                        onConfirm: async () => {
                            try {
                                setActionLoading(prev => ({ ...prev, invite: true }));
                                await clientAPI.invite(clientId);
                                toast.success('Invitation sent successfully');
                                fetchClientDetails();
                            } catch (e) {
                                console.error('Invite error:', e);
                                toast.error(e.response?.data?.message || 'Failed to send invitation');
                            } finally {
                                setActionLoading(prev => ({ ...prev, invite: false }));
                            }
                        }
                    });
                    break;
                }
                case 'delete':
                    setShowDeleteConfirm(true);
                    break;
                case 'appointment':
                    handleCreateAppointment();
                    break;
                default:
                    console.warn('Unknown action type:', actionType);
            }
        } catch (error) {
            console.error('Action error:', error);
            toast.error(`Failed to ${actionType}: ${error.message}`);
        } finally {
            setActionLoading(prev => ({ ...prev, [actionType]: false }));
        }
    };

    const calculateProgress = () => {
        // Use userTasks state instead of client.userTasks for real-time updates
        const tasksToCalculate = userTasks.length > 0 ? userTasks : (client?.userTasks || []);
        
        if (tasksToCalculate.length === 0) {
            return {
                totalTasks: 0,
                completedTasks: 0,
                inProgressTasks: 0,
                notStartedTasks: 0,
                completionPercentage: 0
            };
        }

        const totalTasks = tasksToCalculate.length;
        const completedTasks = tasksToCalculate.filter(task => task.status === 'completed').length;
        const inProgressTasks = tasksToCalculate.filter(task => task.status === 'in_progress').length;
        const notStartedTasks = tasksToCalculate.filter(task => task.status === 'not_started').length;
        const completionPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

        return {
            totalTasks,
            completedTasks,
            inProgressTasks,
            notStartedTasks,
            completionPercentage
        };
    };

    // Prepare task status data for chart - must be before any early returns
    const taskStatusData = useMemo(() => {
        const tasksToCalculate = userTasks.length > 0 ? userTasks : (client?.userTasks || []);
        const statusCounts = {
            'Completed': tasksToCalculate.filter(task => task.status === 'completed').length,
            'In Progress': tasksToCalculate.filter(task => task.status === 'in_progress').length,
            'Not Started': tasksToCalculate.filter(task => task.status === 'not_started').length,
            'Pending': tasksToCalculate.filter(task => task.status === 'pending').length,
            'Overdue': tasksToCalculate.filter(task => task.status === 'overdue').length
        };
        
        return Object.entries(statusCounts)
            .filter(([_, count]) => count > 0)
            .map(([name, value]) => ({ name, value }));
    }, [userTasks, client?.userTasks]);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: branding.primaryColor }}></div>
            </div>
        );
    }

    if (error || !client) {
        return (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
                <div className="border px-6 py-4 rounded-lg max-w-md text-center" style={{ 
                    backgroundColor: `${branding.accentColor}20`,
                    borderColor: `${branding.accentColor}40`,
                    color: branding.accentColor
                }}>
                    <h3 className="font-semibold mb-2">Unable to load client</h3>
                    <p className="text-sm mb-4">{error || 'Client not found'}</p>
                    <div className="flex gap-2">
                        <button
                            onClick={() => fetchClientDetails()}
                            className="px-4 py-2 text-white rounded-lg hover:opacity-90 text-sm"
                            style={{ backgroundColor: branding.accentColor }}
                        >
                            Retry
                        </button>
                        <button
                            onClick={() => {
                                if (fromAccount) {
                                    navigate(`/accounts/${fromAccount}`);
                                } else {
                                    navigate('/clients');
                                }
                            }}
                            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
                        >
                            Back to {fromAccount ? 'Account' : 'Clients'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Get status config with fallback for unknown statuses
    const getStatusConfig = (status) => {
        if (!status) return null;
        
        // Try exact match first
        if (statusConfig[status]) {
            return statusConfig[status];
        }
        
        // Try case-insensitive match
        const normalizedStatus = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
        if (statusConfig[normalizedStatus]) {
            return statusConfig[normalizedStatus];
        }
        
        // Try lowercase match
        if (statusConfig[status.toLowerCase()]) {
            return statusConfig[status.toLowerCase()];
        }
        
        // Return default config for unknown statuses
        return {
            color: 'gray',
            bgColor: 'bg-muted',
            borderColor: 'border-border',
            textColor: 'text-foreground',
            icon: ClockIcon,
            nextStatus: null,
            recommendations: ['Contact support for status guidance']
        };
    };
    
    const lifecycleStatus = client?.lifecycleStatus || client?.status || '';
    const currentStatusConfig = getStatusConfig(lifecycleStatus);
    const progress = calculateProgress();
    
    // Helper function to get client full name (supports both firstName/lastName and name for backward compatibility)
    const getClientName = (clientData) => {
        if (!clientData) return '';
        if (clientData.firstName || clientData.lastName) {
            return `${clientData.firstName || ''} ${clientData.lastName || ''}`.trim();
        }
        return clientData.name || clientData.fullName || '';
    };
    
    const clientName = getClientName(client);
    
    // Debug client data
    console.log('🔍 Client state in render:', {
        client,
        hasClient: !!client,
        clientName: clientName,
        clientEmail: client?.email,
        clientStatus: client?.status,
        clientKeys: client ? Object.keys(client) : 'No client object',
        clientId: clientId
    });

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-card rounded-lg shadow-sm border border-border">
                <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-border">
                    <div className="flex flex-col gap-3 sm:gap-4">
                        {/* Top row: Back button + Client info + Centered badges + Actions */}
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0 w-full">
                            <button
                                onClick={() => {
                                    if (fromAccount) {
                                        navigate(`/accounts/${fromAccount}`);
                                    } else {
                                        navigate('/clients');
                                    }
                                }}
                                className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors flex-shrink-0"
                                aria-label="Go back"
                            >
                                <ArrowLeftIcon className="h-5 w-5" />
                            </button>
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                                {client?.profileImage ? (
                                    <img
                                        src={client.profileImage}
                                        alt={getClientName(client) || 'Client'}
                                        className="h-12 w-12 sm:h-16 sm:w-16 rounded-lg object-cover border-2 border-border flex-shrink-0"
                                    />
                                ) : (
                                    <div className="p-2 sm:p-3 rounded-lg flex-shrink-0" style={{ backgroundColor: `${branding.primaryColor}20` }}>
                                        <UserIcon className="h-6 w-6 sm:h-8 sm:w-8" style={{ color: branding.primaryColor }} />
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-foreground truncate">{getClientName(client) || 'Unknown Client'}</h1>
                                    <p className="text-xs sm:text-sm text-muted-foreground truncate">{client?.email || 'No email provided'}</p>
                                    {/* badges moved to header center; keep personal info compact */}
                                    {client?.clientPartnerUserId && (
                                        <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1 truncate">
                                            Partner: <span className="font-medium text-foreground">{client.clientPartnerUserId.name || 'Partner'}</span>
                                        </p>
                                    )}
                                </div>
                            </div>
                            {/* Centered badges */}
                            <div className="flex-1 hidden sm:flex justify-center">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <StatusBadge status={String(lifecycleStatus).toLowerCase()} type="client" />
                                    {(() => {
                                        const hasUser = !!(client?.userId || client?.portalAccess?.enabled);
                                        if (hasUser) {
                                            return (
                                                <span className="inline-flex items-center gap-1.5 rounded-full border font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 bg-emerald-50 text-emerald-700 border-emerald-200 px-2.5 py-1 text-sm">
                                                    Registered
                                                </span>
                                            );
                                        }
                                        if (client?.portalAccess?.invitedAt || client?.invitedAt) {
                                            return (
                                                <span className="inline-flex items-center gap-1.5 rounded-full border font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 bg-amber-50 text-amber-700 border-amber-200 px-2.5 py-1 text-sm">
                                                    Invited
                                                </span>
                                            );
                                        }
                                        return (
                                            <span className="inline-flex items-center gap-1.5 rounded-full border font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 bg-muted text-foreground border-border px-2.5 py-1 text-sm">
                                                Not invited
                                            </span>
                                        );
                                    })()}
                                </div>
                            </div>
                            
                            {/* Action buttons - Right side */}
                            <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
                                <button
                                    onClick={() => handleAction('edit')}
                                    className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-white rounded-lg hover:opacity-90 flex-shrink-0 transition-all"
                                    style={{ backgroundColor: branding.primaryColor }}
                                    title="Edit Client"
                                >
                                    <PencilIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                    <span className="hidden sm:inline">Edit</span>
                                </button>
                                <button
                                    onClick={() => setShowMessageModal(true)}
                                    className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex-shrink-0"
                                    title="Message Client"
                                >
                                    <EnvelopeIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                    <span className="hidden sm:inline">Message</span>
                                    <span className="sm:hidden">Msg</span>
                                </button>
                                
                                {/* Actions Menu */}
                                <Popover open={actionsMenuOpen} onOpenChange={setActionsMenuOpen}>
                                    <PopoverTrigger asChild>
                                        <button
                                            className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-foreground bg-background border border-input rounded-lg hover:bg-muted transition-colors flex-shrink-0"
                                            title="More Actions"
                                        >
                                            <span className="hidden sm:inline">More</span>
                                            <EllipsisHorizontalIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 sm:hidden" />
                                            <ChevronDownIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 hidden sm:block" />
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-56 p-1" align="end">
                                        <div className="space-y-1">
                                            {/* Primary Actions Group */}
                                            <div className="px-2 py-1.5">
                                                <p className="px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                    Primary
                                                </p>
                                                <button
                                                    onClick={() => {
                                                        setActionsMenuOpen(false);
                                                        handleAction('appointment');
                                                    }}
                                                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-foreground rounded-md hover:bg-muted transition-colors"
                                                >
                                                    <CalendarIcon className="h-4 w-4" />
                                                    <span>Create Appointment</span>
                                                </button>
                                            </div>
                                            
                                            <Separator />
                                            
                                            {/* Communication Group */}
                                            <div className="px-2 py-1.5">
                                                <p className="px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                    Communication
                                                </p>
                                                <button
                                                    onClick={() => {
                                                        setActionsMenuOpen(false);
                                                        handleAction('sendEmail');
                                                    }}
                                                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-foreground rounded-md hover:bg-muted transition-colors"
                                                >
                                                    <EnvelopeIcon className="h-4 w-4" />
                                                    <span>Send Email</span>
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        setActionsMenuOpen(false);
                                                        try {
                                                            const { data } = await formsAPI.getAll();
                                                            setAvailableForms(data.data || []);
                                                        } catch (_) {}
                                                        setShowFormInvite(true);
                                                    }}
                                                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-foreground rounded-md hover:bg-muted transition-colors"
                                                >
                                                    <DocumentTextIcon className="h-4 w-4" />
                                                    <span>Send Form</span>
                                                </button>
                                            </div>
                                            
                                            <Separator />
                                            
                                            {/* Navigation Group */}
                                            <div className="px-2 py-1.5">
                                                <p className="px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                    Navigation
                                                </p>
                                                <button
                                                    onClick={() => {
                                                        setActionsMenuOpen(false);
                                                        handleAction('workspace');
                                                    }}
                                                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-foreground rounded-md hover:bg-muted transition-colors"
                                                >
                                                    <BuildingOfficeIcon className="h-4 w-4" />
                                                    <span>Open Workspace</span>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setActionsMenuOpen(false);
                                                        handleAction('journey');
                                                    }}
                                                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-foreground rounded-md hover:bg-muted transition-colors"
                                                >
                                                    <MapPinIcon className="h-4 w-4" />
                                                    <span>View Journey</span>
                                                </button>
                                            </div>
                                            
                                            <Separator />
                                            
                                            {/* Management Group */}
                                            <div className="px-2 py-1.5">
                                                <p className="px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                    Management
                                                </p>
                                                <button
                                                    onClick={() => {
                                                        setActionsMenuOpen(false);
                                                        handleAction('invite');
                                                    }}
                                                    disabled={!!client?.userId}
                                                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-foreground rounded-md hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <UserGroupIcon className="h-4 w-4" />
                                                    <span>{client?.userId ? 'Invited' : 'Invite to Portal'}</span>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setActionsMenuOpen(false);
                                                        handleAction('delete');
                                                    }}
                                                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-red-600 dark:text-red-400 rounded-md hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                    <span>Delete Client</span>
                                                </button>
                                            </div>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Status and Progress */}
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3 overflow-hidden">
                            {client.accountId && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
                                    <BuildingOfficeIcon className="h-4 w-4" />
                                    <span className="truncate max-w-[220px] sm:max-w-[320px] md:max-w-[420px]">
                                    {client.accountId.companyName}
                                    </span>
                                </div>
                            )}
                        </div>
                        {client.status === 'Prospect' && (
                            <button
                                onClick={handleConvertToActive}
                                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
                                style={{ backgroundColor: branding.secondaryColor }}
                            >
                                <CheckCircleIcon className="h-4 w-4" />
                                Convert to Active
                            </button>
                        )}
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-foreground">Task Progress</span>
                            <span className="text-sm text-muted-foreground">{progress.completionPercentage}%</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                            <div 
                                className="h-2 rounded-full transition-all duration-300"
                                style={{ 
                                    width: `${progress.completionPercentage}%`,
                                    backgroundColor: branding.primaryColor
                                }}
                            ></div>
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                            <span>{progress.completedTasks} completed</span>
                            <span>{progress.totalTasks} total</span>
                        </div>
                    </div>
                </div>
            </div>
            {showFormInvite && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
                    <div className="bg-card w-full max-w-md rounded-lg shadow p-4 border border-border">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-lg font-semibold">Send form to client</h2>
                            <button className="px-2 py-1 text-sm" onClick={() => setShowFormInvite(false)}>Close</button>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm text-foreground mb-1">Form</label>
                                <select className="w-full px-3 py-2 border border-input b bg-background text-foregroundg-background text-foreground rounded focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring" value={selectedFormId} onChange={e => setSelectedFormId(e.target.value)}>
                                    <option value="">-- Choose a form --</option>
                                    {(availableForms || []).map(f => (
                                        <option key={f._id} value={f._id}>{f.name} {f.status === 'published' ? '' : '(Draft)'}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="text-right">
                                <button
                                    className="px-3 py-2 rounded-md bg-blue-600 text-white disabled:opacity-50"
                                    disabled={!selectedFormId || !client?.email}
                                    onClick={async () => {
                                        try {
                                            await formsAPI.invite(selectedFormId, [client.email]);
                                            setShowFormInvite(false);
                                        } catch (e) {}
                                    }}
                                >
                                    Send invite
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* AI Copilot Panel */}
            {hasFeature('aiCopilot') && aiSuggestions.length > 0 && (
                <div className="border rounded-lg p-4" style={{ 
                    background: `linear-gradient(to right, ${branding.accentColor}20, ${branding.primaryColor}20)`,
                    borderColor: `${branding.accentColor}40`
                }}>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <SparklesIcon className="h-5 w-5" style={{ color: branding.accentColor }} />
                            <h3 className="font-semibold" style={{ color: branding.accentColor }}>AI Copilot Suggestions</h3>
                        </div>
                        <button
                            onClick={() => setShowAiPanel(!showAiPanel)}
                            className="text-sm hover:opacity-80"
                            style={{ color: branding.accentColor }}
                        >
                            {showAiPanel ? 'Hide' : 'Show All'}
                        </button>
                    </div>
                    
                    {showAiPanel ? (
                        <div className="space-y-3">
                            {aiSuggestions.map((suggestion) => (
                                <div key={suggestion.id} className={`p-3 rounded-lg border ${
                                    suggestion.priority === 'high' ? 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800' :
                                    suggestion.priority === 'medium' ? 'bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800' :
                                    'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800'
                                }`}>
                                    <div className="flex items-start gap-3">
                                        <suggestion.icon className={`h-5 w-5 mt-0.5 ${
                                            suggestion.priority === 'high' ? 'text-red-600' :
                                            suggestion.priority === 'medium' ? 'text-yellow-600' :
                                            'text-blue-600'
                                        }`} />
                                        <div className="flex-1">
                                            <h4 className="font-medium text-foreground">{suggestion.title}</h4>
                                            <p className="text-sm text-muted-foreground mb-2">{suggestion.description}</p>
                                            <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                                                {suggestion.action} →
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <div className="flex -space-x-2">
                                {aiSuggestions.slice(0, 3).map((suggestion, index) => (
                                    <div key={suggestion.id} className={`w-8 h-8 rounded-full border-2 border-background flex items-center justify-center ${
                                        suggestion.priority === 'high' ? 'bg-red-100 dark:bg-red-900' :
                                        suggestion.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900' :
                                        'bg-blue-100 dark:bg-blue-900'
                                    }`}>
                                        <suggestion.icon className={`h-4 w-4 ${
                                            suggestion.priority === 'high' ? 'text-red-600' :
                                            suggestion.priority === 'medium' ? 'text-yellow-600' :
                                            'text-blue-600'
                                        }`} />
                                    </div>
                                ))}
                            </div>
                            <span className="text-sm text-muted-foreground">
                                {aiSuggestions.length} AI suggestions available
                            </span>
                        </div>
                    )}
                </div>
            )}

            {/* Navigation Tabs */}
            <div className="bg-card rounded-lg shadow-sm border border-border">
                <div className="border-b border-border">
                    <div className="overflow-x-auto scrollbar-thin">
                    <nav className="flex space-x-6 sm:space-x-8 px-4 sm:px-6 min-w-max">
                        {[
                            { id: 'overview', label: 'Overview', icon: UserIcon },
                            { id: 'tasks', label: 'Tasks', icon: CheckCircleIcon, count: userTasks.length },
                            { id: 'opportunities', label: 'Opportunities', icon: CurrencyDollarIcon, count: opportunities.length },
                            { id: 'messages', label: 'Messages', icon: EnvelopeIcon, count: unreadMessageCount },
                            { id: 'analytics', label: 'Analytics', icon: ChartBarIcon },
                            { id: 'recommendations', label: 'Recommendations', icon: LightBulbIcon }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-1.5 sm:gap-2 py-3 sm:py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                                    activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                                }`}
                            >
                                <tab.icon className="h-5 w-5" />
                                {tab.label}
                                {tab.count !== undefined && tab.count > 0 && (
                                    <span className="bg-muted text-muted-foreground py-0.5 px-2 rounded-full text-xs">{tab.count}</span>
                                )}
                            </button>
                        ))}
                    </nav>
                    </div>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                    {activeTab === 'overview' && (
                        <div className="space-y-6">
                            {/* Personal Information */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-foreground">Personal Information</h3>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <UserIcon className="h-5 w-5 text-muted-foreground" />
                                            <div>
                                                <div className="text-sm font-medium text-foreground">{getClientName(client)}</div>
                                                <div className="text-sm text-muted-foreground">Full Name</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <EnvelopeIcon className="h-5 w-5 text-muted-foreground" />
                                            <a 
                                                href={`mailto:${client.email}`}
                                                className="text-sm text-blue-600 hover:text-blue-800"
                                            >
                                                {client.email}
                                            </a>
                                        </div>
                                        {client.phone && (
                                            <div className="flex items-center gap-3">
                                                <PhoneIcon className="h-5 w-5 text-muted-foreground" />
                                                <a 
                                                    href={`tel:${client.phone}`}
                                                    className="text-sm text-blue-600 hover:text-blue-800"
                                                >
                                                    {client.phone}
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-foreground">Location & Preferences</h3>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <MapPinIcon className="h-5 w-5 text-muted-foreground" />
                                            <div>
                                                <div className="text-sm font-medium text-foreground">
                                                    {client.countryNationality || 'N/A'} → {client.countryResidence || 'N/A'}
                                                </div>
                                                <div className="text-sm text-muted-foreground">Nationality → Residence</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <GlobeAltIcon className="h-5 w-5 text-muted-foreground" />
                                            <div>
                                                <div className="text-sm font-medium text-foreground">{client.preferredLanguage?.toUpperCase()}</div>
                                                <div className="text-sm text-muted-foreground">Preferred Language</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <CalendarIcon className="h-5 w-5 text-muted-foreground" />
                                            <div>
                                                <div className="text-sm font-medium text-foreground">{client.timeZone}</div>
                                                <div className="text-sm text-muted-foreground">Time Zone</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Location Journey */}
                            <FeatureGate feature="locationMaps">
                                <div className="border border-border rounded-lg overflow-hidden relative isolate" style={{ zIndex: 0 }}>
                                    {(() => {
                                        const residence = client.countryResidence?.trim();
                                        const nationality = client.countryNationality?.trim();
                                        const locations = [
                                            // Residence location
                                            ...(residence ? [{
                                                name: `${getClientName(client)} (Residence)`,
                                                country: residence,
                                                countryCode: residence,
                                                description: `Current residence`,
                                                type: 'residence'
                                            }] : []),
                                            // Nationality location
                                            ...(nationality ? [{
                                                name: `${getClientName(client)} (Nationality)`,
                                                country: nationality,
                                                countryCode: nationality,
                                                description: `Nationality`,
                                                type: 'nationality'
                                            }] : [])
                                        ];
                                        
                                        console.log('🗺️ LocationMapComponent locations:', {
                                            residence,
                                            nationality,
                                            locationsCount: locations.length,
                                            locations
                                        });
                                        
                                        return (
                                            <LocationMapComponent
                                                locations={locations}
                                                height="400px"
                                                className="w-full"
                                                showFlow={true}
                                            />
                                        );
                                    })()}
                                </div>
                            </FeatureGate>
                        </div>
                    )}

                    {activeTab === 'messages' && (
                        <div className="space-y-4">
                            <div className="h-[600px]">
                                <MessageList 
                                    clientId={clientId} 
                                    isAdminView={true} 
                                    showHeader={false}
                                    initialConversationId={conversationParam || null}
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === 'tasks' && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-foreground">Assigned Tasks</h3>
                                <div className="flex gap-2">
                                    <button 
                                        onClick={() => {
                                            // Navigate to tasks with client data in state
                                            navigate('/tasks', { 
                                                state: { 
                                                    clientId: client._id,
                                                    clientName: getClientName(client),
                                                    clientEmail: client.email
                                                }
                                            });
                                        }}
                                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                                    >
                                        <EyeIcon className="h-4 w-4" />
                                        Manage Tasks
                                    </button>
                                    <UsageLimit action="createTask">
                                        <button 
                                            onClick={handleAssignTasksClick}
                                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                                        >
                                            <PlusIcon className="h-4 w-4" />
                                            Assign Tasks
                                        </button>
                                    </UsageLimit>
                                </div>
                            </div>
                            
                            {userTasks.length > 0 ? (
                                <div className="grid gap-4">
                                    {userTasks.map(task => (
                                        <div 
                                            key={task._id}
                                            className="bg-muted rounded-lg p-4 hover:bg-muted/80 transition-colors"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div 
                                                    className="flex-1 cursor-pointer"
                                                    onClick={() => navigate(`/tasks/${task._id}`, { 
                                                        state: { 
                                                            fromClientDetail: true, 
                                                            clientId: clientId 
                                                        } 
                                                    })}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <div className="font-medium text-foreground">{task.title}</div>
                                                        {task.visibleToClient ? (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" title="Visible to client">
                                                                <EyeIcon className="h-3 w-3" />
                                                                Client
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400" title="Not visible to client">
                                                                <EyeSlashIcon className="h-3 w-3" />
                                                                Private
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-muted-foreground">{task.description}</div>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-xs text-muted-foreground">{task.category}</span>
                                                        <span className="text-xs text-muted-foreground">•</span>
                                                        <StatusBadge
                                                            status={task.priority || 'medium'}
                                                            type="priority"
                                                            size="sm"
                                                            showIcon={false}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <StatusDropdown
                                                        value={task.status}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            handleTaskStatusUpdate(task._id, e.target.value);
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        type="task"
                                                        size="sm"
                                                        className="min-w-[120px]"
                                                    />
                                                    <button 
                                                        className="text-blue-600 hover:text-blue-800"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/tasks/${task._id}`, { 
                                                                state: { 
                                                                    fromClientDetail: true, 
                                                                    clientId: clientId 
                                                                } 
                                                            });
                                                        }}
                                                    >
                                                        <EyeIcon className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <CheckCircleIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                                    <p>No tasks assigned to this client yet.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'opportunities' && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-foreground">Client Opportunities</h3>
                                <button
                                    onClick={() => {
                                        // Navigate to opportunity creation with client data
                                        navigate('/opportunities/new', { 
                                            state: { 
                                                clientId: client._id,
                                                clientName: getClientName(client),
                                                clientEmail: client.email
                                            }
                                        });
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity"
                                    style={{ backgroundColor: branding.primaryColor }}
                                >
                                    <PlusIcon className="h-4 w-4" />
                                    Add Opportunity
                                </button>
                            </div>
                            
                            {opportunities.length > 0 ? (
                                <div className="grid gap-4">
                                    {opportunities.map((opportunity, index) => (
                                        <div key={index} className="bg-card border border-border rounded-lg p-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <h4 className="font-medium text-foreground">{opportunity.name}</h4>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-lg font-bold text-[var(--brand-primary)]">${opportunity.value.toLocaleString()}</span>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => {
                                                                // Navigate to edit opportunity
                                                                navigate(`/opportunities/${opportunity._id}/edit`);
                                                            }}
                                                            className="p-1 text-muted-foreground hover:text-blue-600 transition-colors"
                                                            title="Edit opportunity"
                                                        >
                                                            <PencilIcon className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                // Delete opportunity using modal
                                                                openDialog({
                                                                    title: 'Delete Opportunity',
                                                                    description: `Are you sure you want to delete "${opportunity.name}"? This action cannot be undone.`,
                                                                    confirmText: 'Delete',
                                                                    cancelText: 'Cancel',
                                                                    variant: 'danger',
                                                                    onConfirm: () => {
                                                                        opportunityAPI.delete(opportunity._id)
                                                                            .then(() => {
                                                                                setOpportunities(prev => prev.filter(opp => opp._id !== opportunity._id));
                                                                                toast.success('Opportunity deleted successfully');
                                                                            })
                                                                            .catch(error => {
                                                                                console.error('Error deleting opportunity:', error);
                                                                                toast.error('Failed to delete opportunity');
                                                                            });
                                                                    }
                                                                });
                                                            }}
                                                            className="p-1 text-muted-foreground hover:text-red-600 transition-colors"
                                                            title="Delete opportunity"
                                                        >
                                                            <TrashIcon className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                                <span>Stage: {opportunity.stage}</span>
                                                <span>Probability: {opportunity.probability}%</span>
                                                <span>Close Date: {new Date(opportunity.closeDate).toLocaleDateString()}</span>
                                            </div>
                                            {opportunity.accountId && (
                                                <div className="mt-2 flex items-center gap-2 text-sm">
                                                    <BuildingOfficeIcon className="h-4 w-4 text-muted-foreground" />
                                                    <span className="text-muted-foreground">Account:</span>
                                                    <span className="text-[var(--brand-primary)] font-medium">
                                                        {opportunity.accountId.name}
                                                    </span>
                                                </div>
                                            )}
                                            {opportunity.description && (
                                                <p className="text-sm text-muted-foreground mt-2">{opportunity.description}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <CurrencyDollarIcon className="mx-auto h-12 w-12 text-muted-foreground" />
                                    <h3 className="mt-2 text-sm font-medium text-foreground">No opportunities</h3>
                                    <p className="mt-1 text-sm text-muted-foreground">Get started by creating a new opportunity for this client.</p>
                                    <div className="mt-6">
                                        <button
                                            onClick={() => {
                                                navigate('/opportunities/new', { 
                                                    state: { 
                                                        clientId: client._id,
                                                        clientName: getClientName(client),
                                                        clientEmail: client.email
                                                    }
                                                });
                                            }}
                                            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white hover:opacity-90 transition-opacity"
                                            style={{ backgroundColor: branding.primaryColor }}
                                        >
                                            <PlusIcon className="h-4 w-4 mr-2" />
                                            Add Opportunity
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'analytics' && (
                        <FeatureGate feature="advancedAnalytics">
                            <div className="space-y-6">
                                <h3 className="text-lg font-semibold text-foreground">Client Analytics</h3>
                                
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="bg-blue-50 dark:bg-blue-950 rounded-lg p-4">
                                        <div className="flex items-center gap-3">
                                            <CheckCircleIcon className="h-8 w-8 text-blue-600" />
                                            <div>
                                                <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">{progress.completedTasks}</div>
                                                <div className="text-sm text-blue-700 dark:text-blue-300">Completed Tasks</div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-green-50 dark:bg-green-950 rounded-lg p-4">
                                        <div className="flex items-center gap-3">
                                            <ClockIcon className="h-8 w-8 text-green-600" />
                                            <div>
                                                <div className="text-2xl font-bold text-green-900 dark:text-green-100">{progress.inProgressTasks}</div>
                                                <div className="text-sm text-green-700 dark:text-green-300">In Progress</div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-purple-50 dark:bg-purple-950 rounded-lg p-4">
                                        <div className="flex items-center gap-3">
                                            <ChartBarIcon className="h-8 w-8 text-purple-600" />
                                            <div>
                                                <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">{progress.completionPercentage}%</div>
                                                <div className="text-sm text-purple-700 dark:text-purple-300">Completion Rate</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Task Status Chart */}
                                <div className="mt-6 bg-card rounded-lg shadow-sm border border-border p-6 overflow-hidden">
                                    <EnhancedDonutChart
                                        data={taskStatusData}
                                        title="Task Status Distribution"
                                        subtitle="Overview of client's task statuses"
                                        height={350}
                                        helpText="This chart shows the distribution of tasks for this client across different status levels."
                                    />
                                </div>
                            </div>
                        </FeatureGate>
                    )}

                    {activeTab === 'recommendations' && (
                        <div className="space-y-6">
                            <div className="flex items-center gap-3">
                                <LightBulbIcon className="h-6 w-6 text-yellow-600" />
                                <h3 className="text-lg font-semibold text-foreground">
                                    Recommendations for {client.status} Status
                                </h3>
                            </div>
                            
                            <div className="bg-gradient-to-r from-blue-50 dark:from-blue-950 to-indigo-50 dark:to-indigo-950 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
                                <div className="flex items-start gap-4">
                                    <div className={`p-3 rounded-lg ${currentStatusConfig?.bgColor || 'bg-muted'}`}>
                                        {currentStatusConfig?.icon && <currentStatusConfig.icon className={`h-6 w-6 ${currentStatusConfig?.textColor || 'text-foreground'}`} />}
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-foreground mb-2">
                                            Current Status: {client.status}
                                        </h4>
                                        <p className="text-sm text-muted-foreground mb-4">
                                            {currentStatusConfig?.nextStatus 
                                                ? `Ready to move to ${currentStatusConfig.nextStatus} status?`
                                                : 'This is the optimal status for this client.'
                                            }
                                        </p>
                                        
                                        {currentStatusConfig?.nextStatus && (
                                            <button
                                                onClick={() => handleStatusChange(currentStatusConfig.nextStatus)}
                                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                            >
                                                <CheckCircleIcon className="h-4 w-4" />
                                                Move to {currentStatusConfig.nextStatus}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h4 className="font-semibold text-foreground">Action Items</h4>
                                <div className="space-y-3">
                                    {(currentStatusConfig?.recommendations || []).map((recommendation, index) => (
                                        <div key={index} className="flex items-start gap-3 p-4 bg-card rounded-lg border border-border">
                                            <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                                                <span className="text-xs font-semibold text-blue-600">{index + 1}</span>
                                            </div>
                                            <p className="text-sm text-foreground">{recommendation}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Enhanced Action Confirmation Dialog */}
            <ActionConfirmationDialog
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={handleDeleteClient}
                actionType="delete"
                entityName={getClientName(client) || 'this client'}
                entityType="client"
            />

            {/* Client Status Validation Modal */}
            <ClientStatusValidationModal
                isOpen={showStatusValidationModal}
                onClose={() => setShowStatusValidationModal(false)}
                client={client}
                onStatusUpdated={handleStatusUpdated}
                onProceedToTaskAssignment={handleProceedToTaskAssignment}
            />

    {/* Message Composer Modal */}
    {showMessageModal && client && (
        <MessageComposer
            isModal
            conversationId={`conv_${client._id}`}
            recipient={{ id: client._id, name: getClientName(client), email: client.email, model: 'Client' }}
            onMessageSent={() => setShowMessageModal(false)}
            onClose={() => setShowMessageModal(false)}
        />
    )}

    {/* Appointment Creation Modal */}
    {showAppointmentModal && client && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]" onClick={() => setShowAppointmentModal(false)}>
            <div 
                className="bg-card border border-border rounded-lg p-6 w-full max-w-md shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                        <CalendarIcon className="h-5 w-5" style={{ color: branding.primaryColor }} />
                        Create Appointment
                    </h3>
                    <button
                        onClick={() => setShowAppointmentModal(false)}
                        className="text-muted-foreground hover:text-foreground"
                    >
                        <span className="sr-only">Close</span>
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Title <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={appointmentData.title}
                            onChange={(e) => setAppointmentData({ ...appointmentData, title: e.target.value })}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                            placeholder="Appointment title"
                            style={{ '--tw-ring-color': branding.primaryColor }}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Start Date & Time <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="datetime-local"
                            value={appointmentData.startAt}
                            onChange={(e) => setAppointmentData({ ...appointmentData, startAt: e.target.value })}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                            style={{ '--tw-ring-color': branding.primaryColor }}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            End Date & Time <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="datetime-local"
                            value={appointmentData.endAt}
                            onChange={(e) => setAppointmentData({ ...appointmentData, endAt: e.target.value })}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                            style={{ '--tw-ring-color': branding.primaryColor }}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Location
                        </label>
                        <input
                            type="text"
                            value={appointmentData.location}
                            onChange={(e) => setAppointmentData({ ...appointmentData, location: e.target.value })}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                            placeholder="Meeting location"
                            style={{ '--tw-ring-color': branding.primaryColor }}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Description
                        </label>
                        <textarea
                            value={appointmentData.description}
                            onChange={(e) => setAppointmentData({ ...appointmentData, description: e.target.value })}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                            placeholder="Appointment description"
                            rows="3"
                            style={{ '--tw-ring-color': branding.primaryColor }}
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                    <button
                        onClick={() => setShowAppointmentModal(false)}
                        className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-md hover:bg-muted"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSaveAppointment}
                        className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: branding.primaryColor }}
                    >
                        Create Appointment
                    </button>
                </div>
            </div>
        </div>
    )}
        </div>
    );
};

export default ClientDetailEnhanced;
